import { booleanAttribute, Component, DestroyRef, EventEmitter, inject, Input, Optional, Output } from '@angular/core';
import { Contact } from "@models/common/contact";
import { NgbDropdown, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle, NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { ControlValueAccessor, NgControl, ReactiveFormsModule } from "@angular/forms";
import { ValidationErrorComponent } from "@shared/controls/validation-error/validation-error.component";
import { PhonePipe } from "../../../shared/pipes/phonePipe";
import {
  AddRelationshipContactModalComponent
} from "./add-relationship-contact-modal/add-relationship-contact-modal.component";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
  selector: 'app-contacts-select',
  standalone: true,
  imports: [
    NgbDropdown,
    NgbDropdownItem,
    NgbDropdownMenu,
    NgbDropdownToggle,
    ReactiveFormsModule,
    ValidationErrorComponent,
    PhonePipe
  ],
  templateUrl: 'contacts-select.component.html',
  styleUrl: 'contacts-select.component.scss'
})
export class ContactsSelectComponent implements ControlValueAccessor {
  modalService = inject(NgbModal);
  destroyRef = inject(DestroyRef);

  @Input() label = 'Label';
  @Input() placeholder = '';
  @Input({ transform: booleanAttribute }) disabled = false;
  @Input() noOptionsText = 'There are no available contacts.';
  @Input() entityId: string | null = null;
  @Input() allowAddNoRelationshipNewContact = false;
  @Input() newContactRelationshipId: string | null | undefined = null;

  @Input({ required: true, alias: 'contacts' }) set contactsSetter(value: Contact[]) {
    this.contacts = value || [];

    if (this._originValue) {
      this.writeValue(this._originValue);
    }
  }

  @Output() changed = new EventEmitter<string | null>();
  @Output() select = new EventEmitter<Contact | null>();
  @Output() contactCreated = new EventEmitter<Contact>();
  @Output() noRelationshipContactCreated = new EventEmitter<Contact>();

  contacts: Contact[] = [];
  selectedContact: Contact | null = null;
  _originValue: string | null = null;

  constructor(@Optional() protected ngControl: NgControl) {
    if (ngControl) {
      ngControl.valueAccessor = this;
    }
  }

  writeValue(contactId: string | null) {
    this._originValue = contactId;
    this.selectedContact = this.contacts.find((contact) => contact.contactId == contactId) ?? null;
    this.value = this.selectedContact?.contactId ?? null;
  }

  get displayValidationError(): boolean {
    return !!(this.ngControl && this.ngControl.touched && this.ngControl.errors)
      || !!(this.ngControl.control && this.ngControl.control.touched && this.ngControl.control.errors);
  }

  selectOption(contact: Contact): void {
    this.value = contact.contactId;
    this.selectedContact = contact;
    this.select.emit(contact);
  }

  onChange: any = (): void => {
  };
  onTouched: any = (): void => {
  };


  #value: string | null = null;

  get value(): string | null {
    return this.#value;
  }

  set value(value: string | null) {
    this.#value = value;
    this.onChange(this.#value);
    this.changed.emit(this.#value);
  }

  registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onAddNewContact(): void {
    const modalRef = this.modalService.open(AddRelationshipContactModalComponent, { size: 'sm' });
    const instance = modalRef.componentInstance as AddRelationshipContactModalComponent;

    instance.entityId = this.entityId;
    instance.allowAddNoRelationshipNewContact = this.allowAddNoRelationshipNewContact;
    instance.newContactRelationshipId = this.newContactRelationshipId;

    instance.addNewContact
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (contact) => this.contactCreated.emit(contact)
      });

    instance.addNoRelationshipNewContact
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (contact) => this.noRelationshipContactCreated.emit(contact)
      });
  }

  markAsTouched(): void {
    this.ngControl.control?.markAsTouched();
  }
}
